import { backendApi } from "@/services/backend/api";
import { Kind } from "@/services/backend/contacts/types/kind";
import { ServiceContactKindMatch } from "@/services/backend/contacts/types/service-contact-kind-match";
import { ServiceContactKind } from "@/services/backend/contacts/types/service-contact-kind";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";

const contactKindApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    contactsContactKindPut: build.mutation<Kind, Kind>({
      query: (kind) => ({
        url: "/contacts/contact-kind",
        method: "PUT",
        body: kind,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "ContactKind", id: "LIST" },
        { type: "ContactKind", id: request.id },
      ],
    }),
    contactsContactKindShow: build.query<Kind, ShowRequest>({
      query: (request) => ({
        url: "/contacts/contact-kind",
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _e, request) => [
        { type: "ContactKind", id: request.id },
      ],
    }),
    contactsContactKindDelete: build.mutation<Kind, DeleteRequest>({
      query: (request) => ({
        url: "/contacts/contact-kind",
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "ContactKind", id: request.id },
        { type: "ContactKind", id: "LIST" },
      ],
    }),
    contactsContactKindList: build.query<List, ListRequest>({
      query: (request) => ({
        url: "/contacts/contact-kind/list",
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((kind) => ({
                type: "ContactKind" as const,
                id: kind.id,
              })),
              { type: "ContactKind", id: "LIST" },
            ]
          : [{ type: "ContactKind", id: "LIST" }],
    }),
    contactsContactKindPutServiceContactKindMatch: build.mutation<
      ServiceContactKindMatch,
      ServiceContactKindMatch
    >({
      query: (request) => ({
        url: "/contacts/contact-kind/service-contact-kind-match",
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "ServiceContactKindMatch", id: "LIST" },
        { type: "ServiceContactKindMatch", id: key(request) },
      ],
    }),
    contactsContactKindShowServiceContactKindMatch: build.query<
      ServiceContactKindMatch,
      ShowServiceContactKindMatchRequest
    >({
      query: (request) => ({
        url: "/contacts/contact-kind/service-contact-kind-match",
        method: "GET",
        params: request,
      }),
      providesTags: (_result, _e, request) => [
        { type: "ServiceContactKindMatch", id: key(request) },
      ],
    }),
    contactsContactKindDeleteServiceContactKindMatch: build.mutation<
      unknown,
      ServiceContactKindMatch
    >({
      query: (request) => ({
        url: "/contacts/contact-kind/service-contact-kind-match",
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "ServiceContactKindMatch", id: "LIST" },
        { type: "ServiceContactKindMatch", id: key(request) },
      ],
    }),
    contactsContactKindListServiceContactKinds: build.query<
      ServiceContactKindList,
      ListServiceContactKindsRequest
    >({
      query: (request) => ({
        url: "/contacts/contact-kind/service-contact-kind/list",
        method: "GET",
        params: request,
      }),
    }),
  }),
});

function key(request: {
  serviceKind: GSMServiceKind;
  serviceContactKind: ServiceContactKind;
}) {
  return request.serviceKind + request.serviceContactKind;
}

export const {
  useContactsContactKindPutMutation,
  useContactsContactKindShowQuery,
  useContactsContactKindDeleteMutation,
  useContactsContactKindListQuery,
  useContactsContactKindPutServiceContactKindMatchMutation,
  useContactsContactKindShowServiceContactKindMatchQuery,
  useContactsContactKindDeleteServiceContactKindMatchMutation,
  useContactsContactKindListServiceContactKindsQuery,
} = contactKindApi;

export interface ShowRequest {
  id: string;
}

export interface DeleteRequest {
  id: string;
}

export interface List {
  items: Kind[];
  count: number;
}

export interface ServiceContactKindList {
  items: ServiceContactKind[];
  count: number;
}

export type ListRequest = object;

export interface ShowServiceContactKindMatchRequest {
  serviceKind: GSMServiceKind;
  serviceContactKind: ServiceContactKind;
}

export interface ListServiceContactKindsRequest {
  serviceKind: string;
}
